import React, { useState } from "react";
import { initializeApp, deleteApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  sendPasswordResetEmail,
} from "firebase/auth";
import { getFirestore, doc, setDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { auth, firebaseConfig } from "../shared/FirebaseConfig";
import "../shared/Modal.css";

const TITLES = ["Ms.", "Miss", "Mrs.", "Mr.", "Dr.", "Prof.", "Other"];
const ROLES = [
  { value: "teacher", label: "Teacher" },
  { value: "admin", label: "Admin" },
  { value: "security", label: "Security" },
];

// Friendly-but-random temp password (meets Firebase's 6-char minimum).
function makeTempPassword() {
  const words = ["Boon", "Otter", "Maple", "River", "Cedar", "Comet", "Pencil", "Harbor"];
  const w = words[Math.floor(Math.random() * words.length)];
  return `${w}-${Math.floor(1000 + Math.random() * 9000)}`;
}

const AddUserModal = ({ onClose, onCreated, schoolId, schoolName }) => {
  const [title, setTitle] = useState("Ms.");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("teacher");
  const [tempPassword, setTempPassword] = useState(makeTempPassword());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null); // { email, tempPassword }
  const [emailSent, setEmailSent] = useState(false);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!email.trim() || !lastName.trim()) {
      setError("Email and last name are required.");
      return;
    }
    setBusy(true);
    setError(null);

    // Throwaway secondary app so creating the account does NOT sign the admin
    // out of their own session.
    const secondaryApp = initializeApp(firebaseConfig, `add-user-${Date.now()}`);
    try {
      const secondaryAuth = getAuth(secondaryApp);
      const secondaryDb = getFirestore(secondaryApp);

      const cred = await createUserWithEmailAndPassword(secondaryAuth, email.trim(), tempPassword);
      const newUid = cred.user.uid;
      const displayName = (title === "Other"
        ? `${firstName} ${lastName}`
        : `${title} ${lastName}`).trim();
      await updateProfile(cred.user, { displayName });

      // All new-user writes happen on the secondary app — authenticated AS the
      // new user — so the rules (create own user doc; self-add to school) pass.
      await setDoc(doc(secondaryDb, "users", newUid), {
        email: email.trim(),
        uid: newUid,
        title,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName,
        role,
        schoolId,
        schoolName,
      });
      await updateDoc(doc(secondaryDb, "schools", schoolId), {
        teachers: arrayUnion(newUid),
      });

      onCreated?.({
        id: newUid,
        email: email.trim(),
        displayName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
        pendingRole: role,
        schoolId,
        schoolName,
      });
      setCreated({ email: email.trim(), tempPassword });
    } catch (err) {
      console.error("Add user failed:", err);
      setError(
        err?.code === "auth/email-already-in-use"
          ? "That email already has a ClassBoon account — have them join the school from their own account instead."
          : err?.code === "auth/invalid-email"
          ? "That email address looks invalid."
          : err?.code === "auth/weak-password"
          ? "Password must be at least 6 characters."
          : "Couldn't create the account. Please try again."
      );
    } finally {
      await signOut(getAuth(secondaryApp)).catch(() => {});
      await deleteApp(secondaryApp).catch(() => {});
      setBusy(false);
    }
  };

  const handleSendResetLink = async () => {
    try {
      await sendPasswordResetEmail(auth, created.email);
      setEmailSent(true);
    } catch (err) {
      console.error("Reset email failed:", err);
      setError("Couldn't send the set-password email.");
    }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).catch(() => {});

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {!created ? (
          <>
            <h2 style={{ marginTop: 0 }}>Add a user to {schoolName || "your school"}</h2>
            <p style={{ color: "var(--color-secondary)", marginTop: 0 }}>
              Creates their account directly in your school — they just log in, no sign-up needed.
            </p>

            <div className="form-row">
              <label>Title:</label>
              <select value={title} onChange={(e) => setTitle(e.target.value)} style={{ width: "auto" }}>
                {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>First name:</label>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="form-row">
              <label>Last name:</label>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
            <div className="form-row">
              <label>Email:</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="form-row">
              <label>Role:</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} style={{ width: "auto" }}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label>Temp password:</label>
              <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input value={tempPassword} onChange={(e) => setTempPassword(e.target.value)} />
                <button type="button" onClick={() => setTempPassword(makeTempPassword())} style={{ whiteSpace: "nowrap" }}>
                  ↻ New
                </button>
              </span>
            </div>

            {error && <p style={{ color: "var(--color-error, #c0392b)" }}>{error}</p>}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <button type="button" onClick={onClose} disabled={busy} style={{ background: "#fff", color: "#333", border: "1px solid #ccc" }}>
                Cancel
              </button>
              <button type="button" onClick={handleCreate} disabled={busy} style={{ minWidth: 120 }}>
                {busy ? "Creating…" : "Create account"}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ marginTop: 0 }}>✅ Account created</h2>
            <p style={{ color: "var(--color-secondary)", marginTop: 0 }}>
              Share these so they can log in. They can change the password later in Settings.
            </p>
            <div style={{ background: "#f6f8fa", border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
                <span><strong>Email:</strong> {created.email}</span>
                <button type="button" onClick={() => copy(created.email)}>Copy</button>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                <span><strong>Temp password:</strong> {created.tempPassword}</span>
                <button type="button" onClick={() => copy(created.tempPassword)}>Copy</button>
              </div>
            </div>

            <p style={{ fontSize: 13, color: "var(--color-secondary)" }}>
              Prefer they set their own password? Email them a secure link instead:
            </p>
            {emailSent ? (
              <p style={{ color: "green" }}>📧 Set-password email sent to {created.email}.</p>
            ) : (
              <button type="button" onClick={handleSendResetLink}>Email a set-password link</button>
            )}
            {error && <p style={{ color: "var(--color-error, #c0392b)" }}>{error}</p>}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button type="button" onClick={onClose} style={{ minWidth: 100 }}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddUserModal;
